import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { WeightedSkill } from '../types';

interface SkillEditorProps {
  tenderId: string;
  skills: WeightedSkill[];
  onSave: (skills: WeightedSkill[]) => void;
  disabled?: boolean;
}

const WEIGHT_OPTIONS = [
  { value: 0, labelKey: 'skillEditor.weightIrrelevant', color: 'bg-gray-100 text-gray-600 ring-gray-300' },
  { value: 0.3, labelKey: 'skillEditor.weightNiceToHave', color: 'bg-green-100 text-green-800 ring-green-300' },
  { value: 0.6, labelKey: 'skillEditor.weightImportant', color: 'bg-amber-100 text-amber-800 ring-amber-300' },
  { value: 1.0, labelKey: 'skillEditor.weightMandatory', color: 'bg-red-100 text-red-800 ring-red-300' },
] as const;

function closestOption(weight: number): (typeof WEIGHT_OPTIONS)[number] {
  let bestIdx = 0;
  let bestDist = Math.abs(weight - WEIGHT_OPTIONS[0].value);
  for (let i = 1; i < WEIGHT_OPTIONS.length; i++) {
    const dist = Math.abs(weight - WEIGHT_OPTIONS[i].value);
    if (dist < bestDist) {
      bestIdx = i;
      bestDist = dist;
    }
  }
  return WEIGHT_OPTIONS[bestIdx];
}

export default function SkillEditor({
  tenderId: _tenderId,
  skills,
  onSave,
  disabled = false,
}: SkillEditorProps) {
  const { t } = useTranslation();
  const [editedSkills, setEditedSkills] = useState<WeightedSkill[]>(skills);
  const [newSkillName, setNewSkillName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isDirty = JSON.stringify(editedSkills) !== JSON.stringify(skills);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
      setActiveIndex(null);
    }
  }, []);

  useEffect(() => {
    if (activeIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeIndex, handleClickOutside]);

  function handleWeightChange(index: number, weight: number) {
    setEditedSkills((prev) => prev.map((s, i) => (i === index ? { ...s, weight } : s)));
    setActiveIndex(null);
  }

  function handleDeleteSkill(index: number) {
    setEditedSkills((prev) => prev.filter((_, i) => i !== index));
    setActiveIndex(null);
  }

  function handleAddSkill() {
    const trimmed = newSkillName.trim();
    if (trimmed === '') return;
    const exists = editedSkills.some(
      (s) => s.skill.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) return;
    setEditedSkills((prev) => [...prev, { skill: trimmed, weight: 0.6 }]);
    setNewSkillName('');
  }

  async function handleSave() {
    setSaving(true);
    setSaveMessage(null);
    try {
      await onSave(editedSkills);
      setSaveMessage(t('skillEditor.saved'));
    } catch {
      setSaveMessage(t('skillEditor.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-label={t('skillEditor.title')} className="card space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">{t('skillEditor.title')}</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || saving || !isDirty}
            className="btn-primary text-xs py-1 px-2"
            aria-busy={saving}
          >
            {saving ? t('skillEditor.saving') : t('skillEditor.save')}
          </button>
          {saveMessage && (
            <span role="status" aria-live="polite" className="text-xs text-gray-500">
              {saveMessage}
            </span>
          )}
        </div>
      </div>

      {editedSkills.length === 0 && (
        <p className="text-sm text-gray-400">{t('skillEditor.noSkills')}</p>
      )}

      {/* Compact skill pills — click to edit weight */}
      {editedSkills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {editedSkills.map((ws, index) => {
            const opt = closestOption(ws.weight);
            const isActive = activeIndex === index;
            return (
              <span key={ws.skill} className="relative">
                <button
                  type="button"
                  onClick={() => setActiveIndex(isActive ? null : index)}
                  disabled={disabled}
                  className={[
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 cursor-pointer transition-shadow',
                    opt.color,
                    isActive ? 'ring-2 shadow-sm' : '',
                  ].join(' ')}
                  aria-label={`${ws.skill} — ${t(opt.labelKey)}. ${t('skillEditor.clickToEdit')}`}
                  aria-expanded={isActive}
                >
                  {ws.skill}
                </button>

                {/* Weight popover */}
                {isActive && (
                  <div
                    ref={popoverRef}
                    role="dialog"
                    aria-label={t('skillEditor.editWeight', { skill: ws.skill })}
                    className="absolute z-20 top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-2 w-44"
                  >
                    <p className="text-[10px] text-gray-400 mb-1.5 font-medium uppercase tracking-wider">
                      {t('skillEditor.weight')}
                    </p>
                    <div className="flex flex-col gap-0.5">
                      {WEIGHT_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => handleWeightChange(index, o.value)}
                          className={[
                            'text-left text-xs px-2 py-1 rounded transition-colors',
                            closestOption(ws.weight).value === o.value
                              ? 'bg-brand-50 text-brand-700 font-medium'
                              : 'hover:bg-gray-50 text-gray-700',
                          ].join(' ')}
                        >
                          <span className={[
                            'inline-block w-2 h-2 rounded-full mr-1.5 ring-1',
                            o.color,
                          ].join(' ')} />
                          {t(o.labelKey)}
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 mt-1.5 pt-1.5">
                      <button
                        type="button"
                        onClick={() => handleDeleteSkill(index)}
                        className="text-xs text-red-600 hover:text-red-800 w-full text-left px-2 py-0.5"
                      >
                        {t('skillEditor.deleteSkill', { skill: ws.skill })}
                      </button>
                    </div>
                  </div>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Compact add skill row */}
      <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
        <input
          type="text"
          value={newSkillName}
          onChange={(e) => setNewSkillName(e.target.value)}
          disabled={disabled}
          className="form-input text-xs py-1 px-2 w-36"
          placeholder={t('skillEditor.skillName')}
          aria-label={t('skillEditor.skillName')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddSkill();
            }
          }}
        />
        <button
          type="button"
          onClick={handleAddSkill}
          disabled={disabled || newSkillName.trim() === ''}
          className="btn-secondary text-xs py-1 px-2"
        >
          + {t('skillEditor.addSkill')}
        </button>
      </div>
    </section>
  );
}
